import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DOB_MAX_AGE_YEARS,
  formatDateInputValue,
  getEarliestAllowedDob,
  getLatestAllowedDob,
  validateDateOfBirth,
} from '../utils/validation'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const ITEM_HEIGHT = 44
const WHEEL_HEIGHT = ITEM_HEIGHT * 5

const parseDateValue = (value) => {
  if (!value) return null
  const [year, month, day] = String(value).split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const clampPickerDate = (year, month, day, minDate, maxDate) => {
  const maxDays = new Date(year, month + 1, 0).getDate()
  let nextDay = Math.min(day, maxDays)
  let next = new Date(year, month, nextDay)
  next.setHours(0, 0, 0, 0)

  const min = new Date(minDate)
  const max = new Date(maxDate)
  min.setHours(0, 0, 0, 0)
  max.setHours(0, 0, 0, 0)

  if (next > max) next = max
  if (next < min) next = min

  return {
    year: next.getFullYear(),
    month: next.getMonth(),
    day: next.getDate(),
  }
}

const WheelColumn = ({ items, value, onChange, formatItem = (item) => item }) => {
  const scrollRef = useRef(null)
  const scrollTimeoutRef = useRef(null)

  const scrollToValue = useCallback((nextValue, smooth = false) => {
    const index = items.indexOf(nextValue)
    if (index < 0 || !scrollRef.current) return
    scrollRef.current.scrollTo({
      top: index * ITEM_HEIGHT,
      behavior: smooth ? 'smooth' : 'auto',
    })
  }, [items])

  useEffect(() => {
    scrollToValue(value, false)
  }, [value, items, scrollToValue])

  const handleScroll = () => {
    if (!scrollRef.current) return
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => {
      const index = Math.round(scrollRef.current.scrollTop / ITEM_HEIGHT)
      const clampedIndex = Math.max(0, Math.min(items.length - 1, index))
      const nextValue = items[clampedIndex]
      if (nextValue !== value) onChange(nextValue)
      scrollToValue(nextValue, true)
    }, 80)
  }

  return (
    <div className="relative flex-1 min-w-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-[220px] overflow-y-auto snap-y snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollPaddingTop: ITEM_HEIGHT * 2, scrollPaddingBottom: ITEM_HEIGHT * 2 }}
      >
        <div style={{ height: ITEM_HEIGHT * 2 }} />
        {items.map((item) => {
          const selected = item === value
          return (
            <div
              key={String(item)}
              className={`flex items-center justify-center snap-center transition-all ${
                selected ? 'text-xl font-semibold text-gray-900' : 'text-base text-gray-300'
              }`}
              style={{ height: ITEM_HEIGHT }}
            >
              {formatItem(item)}
            </div>
          )
        })}
        <div style={{ height: ITEM_HEIGHT * 2 }} />
      </div>
    </div>
  )
}

const DateOfBirthPicker = ({
  value = '',
  onChange,
  error = '',
  required = false,
  label = 'Date of Birth',
  id = 'dateOfBirth',
  className = '',
  enforceMinAge = false,
  compact = false,
}) => {
  const [open, setOpen] = useState(false)
  const [localError, setLocalError] = useState('')

  useBodyScrollLock(open)

  const minAgeYears = enforceMinAge ? 13 : 0
  const maxDate = useMemo(() => getLatestAllowedDob(minAgeYears), [minAgeYears])
  const minDate = useMemo(() => getEarliestAllowedDob(DOB_MAX_AGE_YEARS), [])

  const selectedDate = useMemo(() => parseDateValue(value), [value])
  const defaultParts = useMemo(
    () => clampPickerDate(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), minDate, maxDate),
    [maxDate, minDate],
  )

  const [pickerYear, setPickerYear] = useState(defaultParts.year)
  const [pickerMonth, setPickerMonth] = useState(defaultParts.month)
  const [pickerDay, setPickerDay] = useState(defaultParts.day)

  const yearOptions = useMemo(() => {
    const years = []
    for (let year = maxDate.getFullYear(); year >= minDate.getFullYear(); year -= 1) {
      years.push(year)
    }
    return years
  }, [minDate, maxDate])

  const dayOptions = useMemo(() => {
    const count = new Date(pickerYear, pickerMonth + 1, 0).getDate()
    const days = []
    for (let day = 1; day <= count; day += 1) {
      const date = new Date(pickerYear, pickerMonth, day)
      date.setHours(0, 0, 0, 0)
      const min = new Date(minDate)
      const max = new Date(maxDate)
      min.setHours(0, 0, 0, 0)
      max.setHours(0, 0, 0, 0)
      if (date >= min && date <= max) days.push(day)
    }
    return days
  }, [pickerYear, pickerMonth, minDate, maxDate])

  useEffect(() => {
    if (!open) return
    if (selectedDate) {
      const parts = clampPickerDate(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        minDate,
        maxDate,
      )
      setPickerYear(parts.year)
      setPickerMonth(parts.month)
      setPickerDay(parts.day)
      return
    }
    setPickerYear(defaultParts.year)
    setPickerMonth(defaultParts.month)
    setPickerDay(defaultParts.day)
    setLocalError('')
  }, [open, selectedDate, minDate, maxDate, defaultParts])

  useEffect(() => {
    if (!dayOptions.includes(pickerDay)) {
      setPickerDay(dayOptions[dayOptions.length - 1] || 1)
    }
  }, [dayOptions, pickerDay])

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  const updateYear = (year) => {
    const parts = clampPickerDate(year, pickerMonth, pickerDay, minDate, maxDate)
    setPickerYear(parts.year)
    setPickerMonth(parts.month)
    setPickerDay(parts.day)
  }

  const updateMonth = (month) => {
    const parts = clampPickerDate(pickerYear, month, pickerDay, minDate, maxDate)
    setPickerYear(parts.year)
    setPickerMonth(parts.month)
    setPickerDay(parts.day)
  }

  const updateDay = (day) => {
    const parts = clampPickerDate(pickerYear, pickerMonth, day, minDate, maxDate)
    setPickerYear(parts.year)
    setPickerMonth(parts.month)
    setPickerDay(parts.day)
  }

  const handleSubmit = () => {
    const nextValue = formatDateInputValue(new Date(pickerYear, pickerMonth, pickerDay))
    const validation = validateDateOfBirth(nextValue, {
      minAge: minAgeYears,
      maxAge: DOB_MAX_AGE_YEARS,
      required,
    })
    if (!validation.isValid) {
      setLocalError(validation.message)
      return
    }
    onChange?.(nextValue)
    setLocalError('')
    setOpen(false)
  }

  const displayError = error || localError

  return (
    <div className={className}>
      <label htmlFor={id} className={`block font-medium text-gray-700 ${compact ? 'text-xs mb-1' : 'text-sm mb-2'}`}>
        {label}{required ? <span className="text-red-500"> *</span> : null}
      </label>

      <button
        id={id}
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full border rounded-lg bg-white text-left flex items-center justify-between gap-3 transition focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent box-border ${
          compact ? 'h-[42px] px-3 text-sm' : 'h-[50px] px-4 text-base'
        } ${
          displayError ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        <span className={`truncate ${displayValue ? 'text-gray-900' : 'text-gray-400'}`}>
          {displayValue || 'Select date of birth'}
        </span>
        <span className={`inline-flex shrink-0 items-center justify-center rounded-md bg-purple-50 text-primary ${
          compact ? 'h-6 w-6' : 'h-7 w-7'
        }`}>
          <i className={`fas fa-calendar-alt ${compact ? 'text-xs' : 'text-sm'}`} />
        </span>
      </button>

      {displayError ? <p className="text-red-500 text-xs mt-1">{displayError}</p> : null}

      {open ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 px-6 py-10 sm:px-10">
          <div
            className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-8 pt-6 pb-4 border-b border-gray-100">
              <p className="text-center text-base font-semibold text-gray-700">
                Date of Birth
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute right-6 top-5 text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="relative px-8 py-6">
              <div
                className="pointer-events-none absolute left-8 right-8 top-1/2 -translate-y-1/2 rounded-xl bg-gray-100"
                style={{ height: ITEM_HEIGHT }}
              />
              <div className="relative z-10 flex items-stretch gap-2" style={{ height: WHEEL_HEIGHT }}>
                <WheelColumn items={yearOptions} value={pickerYear} onChange={updateYear} />
                <WheelColumn
                  items={MONTHS_SHORT.map((_, index) => index)}
                  value={pickerMonth}
                  onChange={updateMonth}
                  formatItem={(monthIndex) => MONTHS_SHORT[monthIndex]}
                />
                <WheelColumn items={dayOptions} value={pickerDay} onChange={updateDay} />
              </div>
            </div>

            <div className="px-8 pb-8 pt-2">
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full rounded-lg bg-secondary py-4 text-base font-semibold text-white uppercase tracking-wide hover:bg-gray-800 transition"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default DateOfBirthPicker
